<script setup lang="ts">
import {computed, onMounted, watch} from 'vue'
import {RouterView, useRouter} from 'vue-router'
import {storeToRefs} from 'pinia'
import {useAppStore} from './stores/app'
import {useTelegramStore} from './stores/telegram'
import {useColorMode} from './composables/useColorMode'
import {getDefaultTaskRoute} from './utils/defaultRoute'
import {Toaster} from '@/components/ui/sonner'
import TelegramAuth from './components/TelegramAuth.vue'
import AppLayout from './components/AppLayout.vue'
import ReloadPrompt from './components/ReloadPrompt.vue'

useColorMode()

const router = useRouter()
const appStore = useAppStore()
const telegramStore = useTelegramStore()
const {isTelegramWebApp} = storeToRefs(telegramStore)
let isInitializingApp = false
let hasInitializedApp = false

async function handleStartParam() {
  const param = telegramStore.startParam
  if (!param) return false

  const match = param.match(/^task_(\d+)$/)
  telegramStore.startParam = null

  if (match) {
    await router.replace(`/task/${match[1]}`)
    return true
  }

  return false
}

function handleSmartRedirect() {
  const current = router.currentRoute.value
  if (current.path !== '/inbox') return
  if (!current.redirectedFrom || current.redirectedFrom.path !== '/') return

  const fallbackRoute = getDefaultTaskRoute(appStore.allTasks)
  if (fallbackRoute !== '/inbox') {
    router.replace(fallbackRoute)
  }
}

async function initializeAuthenticatedApp() {
  if (isInitializingApp || hasInitializedApp) {
    return
  }

  isInitializingApp = true

  try {
    await appStore.initialize()
    hasInitializedApp = true

    const handledStartParam = await handleStartParam()
    if (!handledStartParam) {
      handleSmartRedirect()
    }
  } finally {
    isInitializingApp = false
  }
}

const showAuthScreen = computed(() => {
  return !isTelegramWebApp.value && !telegramStore.isAuthenticated()
})

onMounted(async () => {
  telegramStore.initialize()
  await new Promise((resolve) => setTimeout(resolve, 50))
  if (telegramStore.isAuthenticated()) {
    await initializeAuthenticatedApp()
  }
})

watch(
  () => telegramStore.isAuthenticated(),
  async (isAuth, wasAuth) => {
    if (!isAuth) {
      hasInitializedApp = false
      return
    }

    if (isAuth && !wasAuth) {
      await initializeAuthenticatedApp()
    }
  }
)
</script>

<template>
  <ReloadPrompt />
  <Toaster position="top-center" :expand="false" rich-colors />
  <TelegramAuth v-if="showAuthScreen" />
  <AppLayout v-else>
    <RouterView />
  </AppLayout>
</template>
